import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { Helper } from "src/app/core/helpers/helper";

declare let html2pdf: any;

export interface InvoiceElements {
  strTitle: string;
  strAmount: string;
  strSubTitle: string;
}

@Component({
  selector: "app-booking-invoice",
  templateUrl: "./booking-invoice.component.html",
  styleUrl: "./booking-invoice.component.scss",
  standalone: false
})
export class BookingInvoiceComponent implements OnChanges {
  totalTax: number = 0;
  distanceConversionValue: number = 0.001;
  decimalPointValue: number = 0;
  setCurrencySign: number = 1;
  paymentStatus!: number;

  distanceUnitString!: string;
  currencySign!: string;
  paymentModeString: string = "";

  bookingInvoiceDetails: any;
  estimatedBookingInvoiceDetails: any;

  arrInvoiceElement: Array<any> = [];

  @Input() bookingTimezone!: any;
  @Input() bookingDetails!: any;
  @Input() bookingHistoryMainType!: any;
  @Input() accessibilityList: Array<any> = [];
  @Input() customPrices: Array<any> = [];
  @Input() isShowEstimatedInvoice!: boolean;
  @Input() invoiceType!: string;

  constructor(public helper: Helper) {}

  /**
   * Handles changes to the input properties of the component.
   * Updates the booking invoice details, currency sign, payment mode, and other properties based on the changes.
   * Also calculates the total tax and updates the invoice elements.
   *
   * @param changes The changes to the input properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes && this.bookingDetails) {
      this.estimatedBookingInvoiceDetails = this.bookingDetails.bookingInvoice?.estimated;
      this.bookingInvoiceDetails = this.isShowEstimatedInvoice ? this.bookingDetails.bookingInvoice?.estimated : this.bookingDetails.bookingInvoice?.actual;
      this.decimalPointValue = this.bookingDetails.decimalPointValue ?? this.bookingDetails.bookingInvoice?.decimalPointValue;
      this.setCurrencySign = this.bookingDetails.setCurrencySign ?? this.bookingDetails.bookingInvoice?.setCurrencySign;
      this.currencySign = this.bookingDetails.bookingInvoice?.currencySign;
      this.paymentModeString = this.helper.PaymentModeStrings[this.bookingDetails.bookingInvoice?.paymentMode];
      this.paymentStatus = this.bookingDetails.bookingInvoice?.paymentStatus;

      this.distanceConversion();

      this.arrInvoiceElement = this.getInvoiceDetails();

      this.totalTax = this.bookingInvoiceDetails.taxPrices?.reduce((total: number, tax: any) => total + tax.price, 0);
    }
  }

  /**
   * Converts the distance based on the distance unit (KM or miles) and sets the distance unit string.
   */
  distanceConversion(): void {
    if (this.bookingDetails.bookingInvoice?.distanceUnit === this.helper.DistanceUnit.KM) {
      this.distanceConversionValue = 0.001;
      this.distanceUnitString = "label-title.km";
    } else {
      this.distanceConversionValue = 0.000621371;
      this.distanceUnitString = "label-title.mile";
    }
  }

  /**
   * Retrieves and processes the invoice details, including charges, additional prices, and accessibility prices.
   * Formats the invoice elements and returns them as an array.
   *
   * @returns An array of formatted invoice elements.
   */
  getInvoiceDetails(): Array<any> {
    let arrInvoiceElement: Array<any> = [];

    // Process charges
    this.bookingInvoiceDetails?.charges?.forEach((obj: any) => {
      const element = this.getStringFromPriceType(obj, this.helper.ChargeType.charges);
      if (element) arrInvoiceElement.push(element);
    });

    // Process additionalPrices
    this.bookingInvoiceDetails?.additionalPrices?.forEach((obj: any) => {
      const element = this.getStringFromPriceType(obj, this.helper.ChargeType.additionalPrices);
      if (element) arrInvoiceElement.push(element);
    });

    // Process accessibilityPrices if not fare estimate
    this.bookingInvoiceDetails?.accessibilityPrices?.forEach((obj: any) => {
      const element = this.getStringFromPriceType(obj, this.helper.ChargeType.accessibilityPrices);
      if (element) arrInvoiceElement.push(element);
    });

    arrInvoiceElement = this.setFormatedElements(arrInvoiceElement);

    return arrInvoiceElement;
  }

  /**
   * Generates a string representation of a price type, including the title and formatted price.
   * Handles different cases for accessibility prices, custom prices, and other price types.
   *
   * @param price The price object.
   * @param chargeType The type of charge (e.g., charges, additionalPrices, accessibilityPrices).
   * @returns An object containing the title, amount, and subtitle of the price, or null if the price is not valid.
   */
  getStringFromPriceType(price: any, chargeType: string): any {
    if ((price.price ?? 0) <= 0) {
      return null;
    }

    let title: any = price.title ?? "";
    if (chargeType === this.helper.ChargeType.accessibilityPrices) {
      title = this.getTitleForAccessibility(price);
    } else if (chargeType === this.helper.ChargeType.additionalPrices) {
      title = this.getTitleForCustomPrice(price);
    }
    const titleValue: string = title ? this.helper.translateService.instant(this.helper.PriceParamEnumString[title] ?? title) : ""; // Assuming this is a method to localize strings
    const priceValue: string = this.formatPrice(price.price ?? 0); // Assuming this is a method to format prices

    if (this.isSimpleTitleCase(title)) {
      return this.getTitles(price, titleValue, priceValue);
    }

    if (this.isTimeDistanceCase(title)) {
      return this.getTimeDistancePriceElement(this.isDistancePrice(title), price, titleValue, priceValue);
    }

    if (title === this.helper.PriceParamEnum.surgePrice) {
      return this.getSurgePriceObject(titleValue, priceValue, price);
    }

    if (title) {
      return { strTitle: titleValue, strAmount: priceValue, strSubTitle: "" };
    }

    return null;
  }

  /**
   * Generates the title and price for simple title cases, such as promo bonuses.
   *
   * @param price The price object.
   * @param titleValue The localized title value.
   * @param priceValue The formatted price value.
   * @returns An object containing the title, amount, and subtitle of the price.
   */
  getTitles(price: any, titleValue: string, priceValue: string): any {
    if (price.title === this.helper.PriceParamEnum.promoBonus) {
      if (this.bookingDetails.bookingInvoice?.promoDetail?.code) {
        const promoTitle: string = `${titleValue} (${this.bookingDetails.bookingInvoice?.promoDetail?.code})`;
        return this.getSimpleTitleCaseObject(promoTitle, priceValue);
      }
      return this.getSimpleTitleCaseObject(titleValue, priceValue);
    }
    return this.getSimpleTitleCaseObject(titleValue, priceValue);
  }

  /**
   * Checks if the given title is a simple title case.
   *
   * @param title The title to check.
   * @returns True if the title is a simple title case, false otherwise.
   */
  isSimpleTitleCase(title: any): boolean {
    const simpleTitleCases = [
      this.helper.PriceParamEnum.bookingFee,
      this.helper.PriceParamEnum.minimumFee,
      this.helper.PriceParamEnum.cancellationPrice,
      this.helper.PriceParamEnum.promoBonus,
      ""
    ];
    return simpleTitleCases.includes(title);
  }

  /**
   * Creates an object for simple title cases, containing the title and price.
   *
   * @param titleValue The localized title value.
   * @param priceValue The formatted price value.
   * @returns An object containing the title, amount, and subtitle of the price.
   */
  getSimpleTitleCaseObject(titleValue: string, priceValue: string): InvoiceElements {
    return { strTitle: titleValue, strAmount: priceValue, strSubTitle: "" };
  }

  /**
   * Checks if the given title is a time or distance case.
   *
   * @param title The title to check.
   * @returns True if the title is a time or distance case, false otherwise.
   */
  isTimeDistanceCase(title: any): boolean {
    const timeDistanceCases = [
      this.helper.PriceParamEnum.distancePrice,
      this.helper.PriceParamEnum.timePrice,
      this.helper.PriceParamEnum.waitingTimePrice,
      this.helper.PriceParamEnum.airportWaitingTimePrice,
      this.helper.PriceParamEnum.stopWaitingTimePrice,
      this.helper.PriceParamEnum.trafficTimePrice
    ];
    return timeDistanceCases.includes(title);
  }

  /**
   * Checks if the given title is a distance price.
   *
   * @param title The title to check.
   * @returns True if the title is a distance price, false otherwise.
   */
  isDistancePrice(title: any): boolean {
    return title === this.helper.PriceParamEnum.distancePrice;
  }

  /**
   * Creates an object for surge prices, containing the title, price, and subtitle.
   *
   * @param titleValue The localized title value.
   * @param priceValue The formatted price value.
   * @param price The price object.
   * @returns An object containing the title, amount, and subtitle of the surge price.
   */
  getSurgePriceObject(titleValue: string, priceValue: string, price: any): InvoiceElements {
    const subString = this.getSurgePriceSubString(price);
    return { strTitle: titleValue, strAmount: priceValue, strSubTitle: subString };
  }

  /**
   * Creates an object for time or distance prices, including the unit and slot prices if applicable.
   *
   * @param isDistance Indicates if the price is a distance price.
   * @param price The price object.
   * @param titleValue The localized title value.
   * @param priceValue The formatted price value.
   * @returns An object containing the title, amount, and subtitle of the time or distance price.
   */
  getTimeDistancePriceElement(isDistance: boolean, price: any, titleValue: string, priceValue: string): InvoiceElements {
    const strUnit: string = isDistance ? this.helper.translateService.instant(this.distanceUnitString) : this.helper.translateService.instant("label-title.min");

    if (price.isApplySlotPrice) {
      let strSubTitle: string = "";

      price.appliedSlots?.forEach((obj: any, i: number) => {
        const min: number = obj.min ?? 0;
        const max: number = obj.max ?? 0;
        const isLast: any = i === (price.appliedSlots?.length ?? 0) - 1;
        const unitValue: any = this.formatPrice(obj.unitPrice ?? 0);
        const slotRange: any = this.getSlotRange(min, max, strUnit);
        const slotPriceValue: any = this.formatPrice(obj.price ?? 0);
        const strSub: string = this.getStrSub(price, unitValue, strUnit);
        strSubTitle = this.getStrSubTitle(price, slotRange, strSub, slotPriceValue, min, max, isLast, strSubTitle);
      });

      return { strTitle: titleValue, strAmount: priceValue, strSubTitle: strSubTitle };
    }

    const unitValue = this.formatPrice(price.unitPrice ?? 0);
    const strSubTitle = `${unitValue}/${strUnit}`;

    return {
      strTitle: titleValue,
      strAmount: priceValue,
      strSubTitle: strSubTitle
    };
  }

  /**
   * Generates the slot range string for a given minimum and maximum value.
   *
   * @param min The minimum value.
   * @param max The maximum value.
   * @param strUnit The unit string (e.g., km, mile).
   * @returns The slot range string.
   */
  getSlotRange(min: any, max: number, strUnit: string): string {
    const maxString = max === this.helper.backendMaxInt ? "∞" : max.toString();
    return `${min} ${strUnit} - ${maxString} ${strUnit}`;
  }

  /**
   * Generates the subtitle string for a given price and unit.
   *
   * @param price The price object.
   * @param unitValue The formatted unit price value.
   * @param strUnit The unit string (e.g., km, mile).
   * @returns The subtitle string.
   */
  getStrSub(price: any, unitValue: string, strUnit: string): string {
    let strSub = `${unitValue}/${strUnit}`;

    if (!price.isSlotInPriceWithUnitCalculation) {
      strSub = unitValue;
    }

    return strSub;
  }

  /**
   * Generates the subtitle string for a given slot range and price.
   *
   * @param price The price object.
   * @param slotRange The slot range string.
   * @param strSub The subtitle string.
   * @param priceValue The formatted price value.
   * @param min The minimum value.
   * @param max The maximum value.
   * @param isLast Indicates if this is the last slot.
   * @param strSubTitle The current subtitle string.
   * @returns The updated subtitle string.
   */
  // eslint-disable-next-line max-params
  getStrSubTitle(price: any, slotRange: any, strSub: string, priceValue: any, min: number, max: number, isLast: any, strSubTitle: string): string {
    if (price.isSlotInPriceWithSum) {
      strSubTitle += `${slotRange} ( ${strSub} ):${priceValue}${isLast ? "" : "\n"}`;
    } else if ((price.unit ?? 0) >= min && (price.unit ?? 0) <= max && !isLast) {
      strSubTitle += `${slotRange} ( ${strSub} ):${priceValue}${isLast ? "" : "\n"}`;
      return strSubTitle;
    } else if (isLast) {
      strSubTitle += `${slotRange} ( ${strSub} ):${priceValue}${isLast ? "" : "\n"}`;
    }

    return strSubTitle;
  }

  /**
   * Generates the subtitle string for surge prices.
   *
   * @param price The price object.
   * @returns The subtitle string for surge prices.
   */
  getSurgePriceSubString(price: any): string {
    let subString: string = "";

    if (price?.unit > 0) {
      subString = `${price.unit} X`;
    }

    return subString;
  }

  /**
   * Formats the given amount as a price string, including the currency sign.
   *
   * @param amount The amount to format.
   * @returns The formatted price string.
   */
  formatPrice(amount: number): string {
    let priceString: string = "";
    const price: string = amount.toFixed(this.decimalPointValue);

    if (this.setCurrencySign === this.helper.SetCurrencySignUnit.LEFT) {
      priceString = `${this.currencySign} ${price}`;
    } else {
      priceString = `${price} ${this.currencySign}`;
    }

    return priceString;
  }

  /**
   * Retrieves the title for accessibility prices based on the charge ID.
   *
   * @param price The price object.
   * @returns The title for the accessibility price.
   */
  getTitleForAccessibility(price: any): string {
    const title = this.accessibilityList?.find((pref) => pref._id === price.chargeId);

    let accessibility = "";
    if (title?.accessibility) {
      accessibility = title?.accessibility;
      // accessibility = title?.accessibility[this.helper.cuurentLang];
    } else if (price.title) {
      accessibility = price.title;
    }
    return accessibility;
  }

  /**
   * Retrieves the title for custom prices based on the charge ID.
   *
   * @param price The price object.
   * @returns The title for the custom price.
   */
  getTitleForCustomPrice(price: any): string {
    const customPrice = this.customPrices?.find((pref) => pref._id === price.chargeId);

    let customPriceTitle = "";
    if (customPrice?.title) {
      customPriceTitle = customPrice?.title;
      // customPriceTitle = customPrice?.title[this.helper.cuurentLang];
    }
    return customPriceTitle;
  }

  /**
   * Formats the invoice elements by splitting the subtitle into an array of title-value pairs.
   *
   * @param arrInvoiceElement The array of invoice elements.
   * @returns The formatted array of invoice elements.
   */
  setFormatedElements(arrInvoiceElement: Array<any>): Array<any> {
    return arrInvoiceElement.map((item) => {
      if (!item.strSubTitle) {
        return { ...item, strSubTitleArray: [] };
      }

      const subtitleArray = item.strSubTitle.split("\n");
      const strSubTitleArray = subtitleArray.map((subtitle: any) => {
        const [title, value] = subtitle.split(":");
        return { title, value };
      });

      return { ...item, strSubTitleArray };
    });
  }

  /**
   * Checks if the booking is not a rental booking.
   *
   * @returns True if the booking is not a rental booking, false otherwise.
   */
  isNotRentalBooking(): boolean {
    return this.bookingDetails.bookingType !== this.helper.PriceMode.RENTAL;
  }

  /**
   * Checks if the booking is not a quick commerce booking.
   *
   * @returns True if the booking is not a quick commerce booking, false otherwise.
   */
  isNotQuickCommerceBooking(): boolean {
    return this.bookingDetails.bookingType !== this.helper.PriceMode.QUICK_COMMERCE;
  }

  /**
   * Checks if the minimum fare is applied to the booking invoice.
   *
   * @returns True if the minimum fare is applied, false otherwise.
   */
  isMinfareApplied(): boolean {
    return this.bookingInvoiceDetails?.charges.some((item: any) => item.title === this.helper.PriceParamEnum.minimumFee && item.price > 0);
  }

  /**
   * Starts the button loader to indicate a loading state.
   * Prevents multiple loaders from being shown simultaneously.
   */
  startBtnLoader(): void {
    if (this.helper.showBtnLoader[1] === true) {
      return;
    }

    this.helper.showBtnLoader[1] = true;
  }

  /**
   * Downloads the booking invoice as a PDF file.
   * Clones the content element, removes dark mode, and generates the PDF using html2pdf.
   *
   * @returns void
   */
  downloadPdf(): void {
    this.startBtnLoader();

    // Get the content element
    const element = document.getElementById("htmlContent") as HTMLElement;

    // Clone the content element
    const clonedElement = element.cloneNode(true) as HTMLElement;

    // Remove dark mode by temporarily changing theme-related attributes or classes
    clonedElement.removeAttribute("data-bs-theme");

    // Add the desired class to the cloned element (e.g., for light mode styling)
    clonedElement.classList.add("light-mode-pdf"); // Assuming 'light-mode' is a CSS class for light theme

    // Create a hidden container for the cloned element (to avoid affecting the view)
    const hiddenContainer = document.createElement("div");
    hiddenContainer.style.display = "none"; // Ensure it's not visible in the DOM
    hiddenContainer.appendChild(clonedElement);
    document.body.appendChild(hiddenContainer);

    const date = Math.floor(new Date().getTime() / 1000);
    const filename: string = `${date?.toString() + this.bookingDetails.uniqueId.toString()}.pdf`;

    const options = {
      filename: filename,
      margin: [10, 20, 10, 20]
    };

    html2pdf()
      .set(options)
      .from(clonedElement) // Use the cloned element to generate the PDF
      .toPdf()
      .save()
      .then(() => {
        // Cleanup the hidden container after PDF generation
        document.body.removeChild(hiddenContainer);
        this.helper.showBtnLoader[1] = false;
      })
      .catch((error: any) => {
        console.error("Error generating PDF", error);

        // Cleanup the hidden container in case of an error
        document.body.removeChild(hiddenContainer);
        this.helper.showBtnLoader[1] = false;
      });
  }
}
